import React, { useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { toast } from "react-toastify";
import {
  addTransaction,
  type MappedOption,
  type SplitCommonFields,
} from "src/apiService/modules/transactions";
import { updateItem } from "src/store/sale/actions";
import type { AppThunkDispatch } from "src/store/types";
import {
  saveTransactionOptimistic,
  unsplitTransactionOptimistic,
  upsertSplitTransactionOptimistic,
} from "src/store/transaction/actions";
import { getAllSales } from "src/store/sale/selector";
import { parseError } from "src/utils";
import { isMileageAccount } from "./utils";
import { EditTransactionForm } from "./EditTransactionForm";
import type { EditDialogContentWrapperProps, SplitSaveOption } from "./types";

export function EditDialogContentWrapper({
  onClose,
  data,
  onDelete,
  loading: _loading,
  noCopyExpenseButton,
  onMaxWidthChange,
}: EditDialogContentWrapperProps) {
  const [loading, setLoading] = useState(false);
  const [key, setKey] = useState(0);
  const store = useStore();
  const dispatch = useDispatch<AppThunkDispatch>();

  const base = data?.transaction;
  const wasSplit = data?.mode === "split";

  const handleSaveItem = async (
    item: import("src/interfaces").Transaction,
    {
      next = false,
      mapped = undefined,
      split = undefined,
    }: {
      next?: boolean;
      mapped?: MappedOption;
      split?: SplitSaveOption;
    } = {},
    newImageFile?: File
  ) => {
    setLoading(true);

    try {
      const isExpenseOnSale = (item as any)?.type === "sale";
      if (isExpenseOnSale) {
        const saleId = (item as any).sale_id;
        const saleProperty = (item as any).sale_property;
        await dispatch(updateItem({ id: saleId, [saleProperty]: item.amount }));
      } else if (split) {
        // If we're converting an existing non-split transaction into a split
        // group, reuse the original doc id for the first row so history/links
        // stay intact. Linked transactions (sale/inventory/journal/transfer)
        // are blocked from splitting upstream, so there are no stale links to
        // scrub off the reused primary row here.
        const rows = split.rows.map((r, index) => {
          if (
            !r.id &&
            !split.split_group_id &&
            base?.id &&
            index === 0 &&
            !wasSplit
          ) {
            return { ...r, id: base.id };
          }
          return r;
        });

        // Only carry fields that are actually set. Building the shared object
        // explicitly (rather than blanket-stripping undefined later) keeps a
        // genuinely-missing required value from being silently dropped.
        const common: SplitCommonFields = {
          ...(item.date !== undefined && { date: item.date }),
          ...(item.description !== undefined && {
            description: item.description,
          }),
          ...("vendor" in item ? { vendor: item.vendor } : {}),
          ...(item.integration_name !== undefined && {
            integration_name: item.integration_name,
          }),
          ...(item.bank_account_id !== undefined && {
            bank_account_id: item.bank_account_id,
          }),
          ...(item.upload !== undefined && { upload: item.upload }),
        };

        await dispatch(
          upsertSplitTransactionOptimistic({
            split_group_id: split.split_group_id,
            common,
            rows,
            newImageFile,
            existingReceiptFilename: item.receipt_filename,
          })
        );
      } else {
        if (wasSplit) {
          // Drop the old row id and group id. Keep the stored receipt unless
          // the user staged a replacement file; the unsplit function reuses
          // that path on the new row.
          const {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            id: _oldId,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            split_group_id: _oldGroup,
            receipt_filename: existingReceipt,
            ...rest
          } = item as typeof item;
          item = (
            newImageFile
              ? rest
              : {
                  ...rest,
                  ...(existingReceipt && {
                    receipt_filename: existingReceipt,
                  }),
                }
          ) as typeof item;
        }

        const shouldUpdateSale =
          item.orderId &&
          !getAllSales(store.getState()).find(
            (sale) => sale.transaction_id === item.orderId
          );
        let payload = item;
        if (isMileageAccount(item.gl_account_id, item.account)) {
          payload = {
            ...item,
            integration_name: "Owner's Contributions/Draws",
          };
        }
        const saveOptions = {
          ...(mapped ? { mapped } : undefined),
          ...(shouldUpdateSale ? { saleId: item.orderId } : undefined),
        };
        if (wasSplit && data?.split?.split_group_id) {
          // The server writes the single row and deletes the group together.
          // A transfer or sale link is applied afterward, against the new id.
          const newId = await dispatch(
            unsplitTransactionOptimistic(
              data.split.split_group_id,
              payload,
              newImageFile
            )
          );
          if (mapped || shouldUpdateSale) {
            await addTransaction({ ...payload, id: newId }, saveOptions);
          }
        } else {
          await dispatch(
            saveTransactionOptimistic(payload, saveOptions, newImageFile)
          );
        }
      }

      if (item.id || split?.split_group_id) {
        toast.success("Expense has been updated successfully");
      } else {
        toast.success("Expense has been added successfully");
      }
    } catch (error) {
      toast.error(parseError(error));
      return;
    } finally {
      setLoading(false);
    }

    if (next) {
      setKey((k) => k + 1);
    } else {
      onClose(true);
    }
  };

  return (
    <EditTransactionForm
      key={key}
      onSave={handleSaveItem}
      data={key === 0 ? data : undefined}
      loading={_loading || loading}
      onDelete={onDelete}
      noCopyExpenseButton={noCopyExpenseButton}
      onMaxWidthChange={onMaxWidthChange}
    />
  );
}
